import { useState, useCallback } from 'react';
import { apiClient } from '../services/api.client';

interface UseDeleteCaseReturn {
  deleteCase: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | null;
  reset: () => void;
}

export const useDeleteCase = (): UseDeleteCaseReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteCase = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await apiClient.delete(`cases/${id}`);
    } catch (err) {
      let error: Error;

      if (err && typeof err === 'object' && 'response' in err) {
        try {
          const response = (err as { response: Response }).response;

          // Handle specific status codes
          if (response.status === 401) {
            // Check if this is an auth issue or business logic issue
            const errorData = await response.json();
            error = new Error(
              errorData.message ||
                'You do not have permission to delete this case.'
            );
          } else if (response.status === 409) {
            const errorData = await response.json();
            error = new Error(
              errorData.message || 'Cannot delete case with existing references'
            );
          } else {
            const errorData = await response.json();
            error = new Error(errorData.message || 'Failed to delete case');
          }
        } catch {
          error = new Error('Failed to delete case');
        }
      } else if (err instanceof Error) {
        error = err;
      } else {
        error = new Error('Failed to delete case');
      }

      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    deleteCase,
    loading,
    error,
    reset,
  };
};
